/**
 * syncHooks — useEffect-хуки, которые применяют side-effects на MapLibre map
 * при изменении state в useMapStore. Все hooks idempotent и безопасны.
 *
 * Архитектура:
 *   - Map создаётся один раз в MapCanvas.tsx.
 *   - setupMapLayers() добавляет все source/layer сразу (с visibility=none для
 *     mutex layers).
 *   - Эти hooks подписываются на конкретные state-slices и обновляют только
 *     то что меняется (visibility / paint property / source URL).
 *
 * НЕ создаёт / не уничтожает sources — это делает setupMapLayers единожды.
 */
import { useEffect, useRef } from 'react';
import type { Map as MLMap, ExpressionSpecification } from 'maplibre-gl';
import { LAYER_IDS, updateRasterTileUrls, type MvtScope } from './setupMapLayers';
import type { NdviMode } from './useMapStore';
import type { Crop } from './useCropsReference';
import { attachCropIconMissingHandler, buildCropPatternAtlas } from './cropPatterns';

const MUTEX_LAYERS: Record<NdviMode, string | null> = {
  none: null,
  ndvi: LAYER_IDS.ndvi,
  ndvi_contrast: LAYER_IDS.ndviContrast,
  avg_ndvi: LAYER_IDS.avgNdvi,
  uniformity: LAYER_IDS.uniformity,
  gnr: LAYER_IDS.gnr,
};

const ALL_MUTEX_LAYER_IDS: string[] = Object.values(MUTEX_LAYERS).filter(
  (x): x is string => Boolean(x),
);

/** Безопасный setLayoutProperty — игнорирует если layer'а нет (race). */
function safeSetVisibility(map: MLMap, layerId: string, visible: boolean): void {
  if (!map.getLayer(layerId)) return;
  try {
    map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
  } catch (e) {
    if (import.meta.env.DEV) console.warn(`[syncHooks] setVisibility(${layerId}) failed`, e);
  }
}

/**
 * При изменении mode — переключает visibility 4х mutex layers.
 * <50ms (визуальный бюджет).
 */
export function useNdviModeSync(map: MLMap | null, mode: NdviMode): void {
  useEffect(() => {
    if (!map) return;
    const activeLayer = MUTEX_LAYERS[mode];
    for (const id of ALL_MUTEX_LAYER_IDS) {
      safeSetVisibility(map, id, id === activeLayer);
    }
  }, [map, mode]);
}

/**
 * При изменении даты ИЛИ scope — обновляет URL у всех 5 sources (fields-mvt + 4 raster).
 * <100ms (cached) / <500ms cold.
 *
 * AK-71: scope участвует в deps — при смене farm/cluster все raster sources
 * получают scope-aware URL (backend clip'ит растры по геометрии scope).
 * Note: recreateFieldsSource уже вызывается отдельно из MapCanvas useEffect[activeScope]
 * для пересоздания MVT source (setTiles на vector source кеширует тайлы); здесь же
 * для raster sources достаточно `setTiles` — MapLibre перезапросит новые tiles.
 */
export function useDateSync(
  map: MLMap | null,
  selectedDate: string,
  scope?: MvtScope | null,
): void {
  // Сериализуем scope в строку для стабильного deps-сравнения (object identity
  // меняется на каждом render даже при тех же values).
  const scopeKey = scope ? `${scope.type}:${scope.id ?? ''}` : '';
  useEffect(() => {
    if (!map || !selectedDate) return;
    updateRasterTileUrls(map, selectedDate, scope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, selectedDate, scopeKey]);
}

/**
 * Загружает паттерны и иконки культур в map atlas, как только reference
 * подгрузился. Идемпотентен.
 *
 * Также регистрирует styleimagemissing handler — lazy-fallback для тех
 * `crop-icon-{uuid}` которые symbol-layer запрашивает, но atlas ещё / уже
 * не содержит. Без этого MapLibre логирует "Image … could not be loaded"
 * (AK-80, founder QA staging).
 */
export function useCropPatternsSync(map: MLMap | null, crops: Crop[] | undefined): void {
  // Ref на актуальные crops — styleimagemissing handler читает его lazily,
  // не пере-регистрируется на каждом ре-рендере.
  const cropsRef = useRef<Crop[] | undefined>(crops);
  useEffect(() => {
    cropsRef.current = crops;
  }, [crops]);

  // Один раз на map — навешиваем styleimagemissing listener.
  useEffect(() => {
    if (!map) return;
    let detach: (() => void) | null = null;
    const setup = () => {
      detach = attachCropIconMissingHandler(map, () => cropsRef.current);
    };
    if (map.isStyleLoaded()) {
      setup();
    } else {
      map.once('load', setup);
    }
    return () => {
      if (detach) detach();
      map.off('load', setup);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !crops?.length) return;
    // Map может быть ещё не загружен (style not ready) — addImage кинет.
    // Дожидаемся style.load если нужно.
    const apply = () => buildCropPatternAtlas(map, crops);
    if (map.isStyleLoaded()) {
      apply();
    } else {
      map.once('load', apply);
      return () => {
        map.off('load', apply);
      };
    }
  }, [map, crops]);
}

/**
 * Пересобирает paint expression `fill-color` для cropFillGrowing — solid
 * заливка цветом культуры для phase=growing. Match по current_crop_id → color
 * из reference. Без recreate'а layer.
 *
 * v6 (founder 2026-05-04): убран cropColorFill (отдельный solid layer)
 *   — все 3 phase-layers теперь несут цвет культуры (planned/harvested через
 *   pattern, growing через match-expression здесь).
 */
export function useCropFillExpressionSync(
  map: MLMap | null,
  crops: Crop[] | undefined,
): void {
  useEffect(() => {
    if (!map || !crops?.length) return;

    const colorExpr: ExpressionSpecification = [
      'match',
      ['to-string', ['get', 'current_crop_id']],
      ...crops.flatMap((c) => [c.id, c.color] as [string, string]),
      'rgba(156, 163, 175, 0.3)', // fallback (нейтральный серый)
    ] as unknown as ExpressionSpecification;

    if (!map.getLayer(LAYER_IDS.cropFillGrowing)) return;
    try {
      map.setPaintProperty(LAYER_IDS.cropFillGrowing, 'fill-color', colorExpr);
    } catch (e) {
      if (import.meta.env.DEV) console.warn(`[syncHooks] setPaintProperty(${LAYER_IDS.cropFillGrowing}) failed`, e);
    }
  }, [map, crops]);
}

/**
 * Focus mode: при клике на культуру в CropFilterPanel все поля этой культуры
 * становятся ярко-окрашенными (cropFillGrowing включается + bright outline +
 * толще линия), остальные — еле видимые серые контуры. Сброс при focusedCropId === null.
 *
 * BUG (AK-62, founder QA 2026-05-01): раньше hook менял только fill-opacity
 * на 3х cropFill-layer'ах, но они все имеют visibility: 'none' по умолчанию
 * (founder убрал default crop-fill из production-look), поэтому клик
 * визуально ничего не делал. Outline color expression тоже срабатывал, но
 * разница серый ↔ оранжевый была малозаметна без увеличения толщины.
 *
 * Fix: при focus временно включаем cropFillGrowing visibility (он единственный
 * с детерминированным match→color через useCropFillExpressionSync, без pattern
 * атласа который может быть не загружен), показываем bright fill для focused
 * crop. Outline у focused crop становится 2× толще + полностью непрозрачен,
 * у non-focused — серый, тонкий, полупрозрачный.
 */
export function useFocusModeSync(
  map: MLMap | null,
  focusedCropId: string | null,
): void {
  useEffect(() => {
    if (!map) return;
    // Все fill-layers, на которые влияет focus mode (3 phase-layers — все
    // несут цвет культуры через pattern или fill-color, founder v6 2026-05-04).
    const cropFillLayers = [
      LAYER_IDS.cropFillPlanned,
      LAYER_IDS.cropFillGrowing,
      LAYER_IDS.cropFillHarvested,
    ];
    // Базовая opacity для каждого layer'а — соответствует значениям в setupMapLayers.
    const baseOpacityFor = (id: string): number => {
      if (id === LAYER_IDS.cropFillHarvested) return 0.75;
      if (id === LAYER_IDS.cropFillGrowing) return 0.55;
      return 0.65;
    };

    // Базовый line-width expression (тот же что в setupMapLayers).
    const baseLineWidth: ExpressionSpecification = [
      'interpolate', ['linear'], ['zoom'],
      9, 0.8,
      13, 1.6,
      16, 2.4,
    ] as unknown as ExpressionSpecification;

    if (!focusedCropId) {
      // Сброс — возвращаем base opacity. Visibility управляется единым toggle
      // cropColorFillOn через useCropFillSync; здесь фокус-режим только меняет
      // opacity, не visibility.
      for (const id of cropFillLayers) {
        if (!map.getLayer(id)) continue;
        try {
          map.setPaintProperty(id, 'fill-opacity', baseOpacityFor(id));
        } catch {
          /* tolerate */
        }
      }
      if (map.getLayer(LAYER_IDS.fieldsOutline)) {
        try {
          map.setPaintProperty(LAYER_IDS.fieldsOutline, 'line-color', '#FF6D00');
          map.setPaintProperty(LAYER_IDS.fieldsOutline, 'line-width', baseLineWidth);
          map.setPaintProperty(LAYER_IDS.fieldsOutline, 'line-opacity', 0.95);
        } catch {
          /* tolerate */
        }
      }
      return;
    }

    // Focus: только выбранная культура — нормально, остальные dim'ed.
    const isFocusedExpr: ExpressionSpecification = [
      '==', ['to-string', ['get', 'current_crop_id']], focusedCropId,
    ] as unknown as ExpressionSpecification;

    for (const id of cropFillLayers) {
      if (!map.getLayer(id)) continue;
      const opacityExpr: ExpressionSpecification = [
        'case',
        isFocusedExpr,
        baseOpacityFor(id),
        0.05,
      ] as unknown as ExpressionSpecification;
      try {
        map.setPaintProperty(id, 'fill-opacity', opacityExpr);
      } catch {
        /* tolerate */
      }
    }

    if (map.getLayer(LAYER_IDS.fieldsOutline)) {
      const lineColorExpr: ExpressionSpecification = [
        'case', isFocusedExpr, '#FF6D00', 'hsl(25, 5%, 55%)',
      ] as unknown as ExpressionSpecification;
      // Толще линия у focused — визуальный приоритет.
      // MapLibre правило: только ОДИН zoom-based subexpression (interpolate/step
      // по ['zoom']) на expression. Раньше было ['case', isFocused, interp1, interp2]
      // — два interpolate(zoom) внутри case → MapLibre валидация падала с
      // "Only one zoom-based subexpression may be used", и outline вообще
      // переставал рендериться (regression в focus mode).
      // Фикс: один outer interpolate(zoom), внутри stops — case по focus.
      const lineWidthExpr: ExpressionSpecification = [
        'interpolate', ['linear'], ['zoom'],
        9, ['case', isFocusedExpr, 1.6, 0.6],
        13, ['case', isFocusedExpr, 3.0, 1.0],
        16, ['case', isFocusedExpr, 4.0, 1.4],
      ] as unknown as ExpressionSpecification;
      const lineOpacityExpr: ExpressionSpecification = [
        'case', isFocusedExpr, 1.0, 0.4,
      ] as unknown as ExpressionSpecification;
      try {
        map.setPaintProperty(LAYER_IDS.fieldsOutline, 'line-color', lineColorExpr);
        map.setPaintProperty(LAYER_IDS.fieldsOutline, 'line-width', lineWidthExpr);
        map.setPaintProperty(LAYER_IDS.fieldsOutline, 'line-opacity', lineOpacityExpr);
      } catch {
        /* tolerate */
      }
    }
  }, [map, focusedCropId]);
}

/** Toggle "Подписи: Культура" — visibility crop-icons-layer. */
export function useCropIconsToggleSync(map: MLMap | null, on: boolean): void {
  useEffect(() => {
    if (!map) return;
    safeSetVisibility(map, LAYER_IDS.cropIcons, on);
  }, [map, on]);
}

/** Toggle "Подписи: Номер" — visibility field-number-layer. */
export function useFieldNumberToggleSync(map: MLMap | null, on: boolean): void {
  useEffect(() => {
    if (!map) return;
    safeSetVisibility(map, LAYER_IDS.fieldNumber, on);
  }, [map, on]);
}

/** Toggle "Подписи: Название культуры" — visibility crop-name-layer (founder 2026-05-04). */
export function useCropNameToggleSync(map: MLMap | null, on: boolean): void {
  useEffect(() => {
    if (!map) return;
    safeSetVisibility(map, LAYER_IDS.cropName, on);
  }, [map, on]);
}

/** Toggle "Подписи: Площадь поля" — visibility field-area-layer (founder 2026-05-04). */
export function useFieldAreaToggleSync(map: MLMap | null, on: boolean): void {
  useEffect(() => {
    if (!map) return;
    safeSetVisibility(map, LAYER_IDS.fieldArea, on);
  }, [map, on]);
}

/**
 * Unified toggle «По цвету культуры» (founder 2026-05-04 v6) — visibility
 * всех 3х phase-fill layers (planned/growing/harvested) одновременно.
 *
 * Каждый layer рисует цвет культуры со своим pattern по статусу:
 *   - planned   → полосы (crop-stripe-{id} pattern)
 *   - growing   → solid color (через useCropFillExpressionSync)
 *   - harvested → точки (crop-dots-{id} pattern)
 *
 * Раньше было 2 отдельных toggle (useCropColorFillToggleSync для solid layer +
 * usePhaseFillToggleSync для 3х phase-layers) — founder потребовал «селектор
 * один».
 */
export function useCropFillSync(map: MLMap | null, on: boolean): void {
  useEffect(() => {
    if (!map) return;
    safeSetVisibility(map, LAYER_IDS.cropFillPlanned, on);
    safeSetVisibility(map, LAYER_IDS.cropFillGrowing, on);
    safeSetVisibility(map, LAYER_IDS.cropFillHarvested, on);
  }, [map, on]);
}

/** Debug toggle — TCI snapshot. */
export function useTCIToggleSync(map: MLMap | null, on: boolean): void {
  useEffect(() => {
    if (!map) return;
    safeSetVisibility(map, LAYER_IDS.tciSnapshot, on);
  }, [map, on]);
}

/** Debug toggle — Cloud mask. */
export function useCloudMaskToggleSync(map: MLMap | null, on: boolean): void {
  useEffect(() => {
    if (!map) return;
    safeSetVisibility(map, LAYER_IDS.cloudMask, on);
  }, [map, on]);
}
